/**
 * Integration Dashboard - Dashboard principale per monitoraggio integrazione HealthNET-RECUP
 * Visualizza stato servizi, metriche, statistiche e controlli operativi
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  LinearProgress,
  Alert,
  Tabs,
  Tab,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress
} from '@mui/material';
import {
  PlayArrow,
  Stop,
  Refresh,
  Settings,
  Notifications,
  Error,
  Warning,
  CheckCircle,
  Info,
  Sync,
  Schedule,
  Assessment,
  Timeline,
  Storage,
  Cloud,
  Speed,
  Memory,
  NetworkCheck,
  HealthAndSafety
} from '@mui/icons-material';
import { Line, Doughnut, Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip as ChartTooltip,
  Legend,
  ArcElement,
  BarElement
} from 'chart.js';

// Registra componenti Chart.js
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  ChartTooltip,
  Legend,
  ArcElement,
  BarElement
);

interface ServiceStatus {
  status: 'healthy' | 'degraded' | 'unhealthy' | 'unknown';
  uptime: number;
  lastCheck: string;
  responseTime?: number;
  error?: string;
}

interface IntegrationStats {
  requests: number;
  errors: number;
  syncOperations: number;
  bookingOperations: number;
  notifications: number;
  performance: {
    avgResponseTime: number;
    maxResponseTime: number;
    minResponseTime: number;
  };
  services: {
    syncManager: any;
    bookingManager: any;
    notificationService: any;
  };
}

interface HealthStatus {
  status: string;
  timestamp: string;
  uptime: number;
  services: {
    [key: string]: ServiceStatus;
  };
}

const IntegrationDashboard: React.FC = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [serviceStatus, setServiceStatus] = useState<HealthStatus | null>(null);
  const [stats, setStats] = useState<IntegrationStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isRunning, setIsRunning] = useState(false);
  const [configDialog, setConfigDialog] = useState(false);
  const [syncDialog, setSyncDialog] = useState(false);
  const [notifications, setNotifications] = useState<any[]>([]);
  const [performanceData, setPerformanceData] = useState<any[]>([]);

  // Configurazione
  const [config, setConfig] = useState({
    autoSync: true,
    syncInterval: 300000,
    notificationsEnabled: true,
    conflictResolution: 'healthnet_priority'
  });

  // Carica dati iniziali
  useEffect(() => {
    loadDashboardData();
    const interval = setInterval(loadDashboardData, 30000); // Aggiorna ogni 30 secondi
    return () => clearInterval(interval);
  }, []);

  const loadDashboardData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const [statusResponse, statsResponse] = await Promise.all([
        fetch('/api/integration/status', {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        }),
        fetch('/api/integration/stats', {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        })
      ]);

      if (statusResponse.ok) {
        const statusData = await statusResponse.json();
        setServiceStatus(statusData.data);
        setIsRunning(statusData.data.isRunning);
      }

      if (statsResponse.ok) {
        const statsData = await statsResponse.json();
        setStats(statsData.data);
        updatePerformanceData(statsData.data);
      }

    } catch (err) {
      setError(err instanceof Error ? err.message : 'Errore nel caricamento dati');
    } finally {
      setLoading(false);
    }
  }, []);

  const updatePerformanceData = (newStats: IntegrationStats) => {
    const now = new Date();
    setPerformanceData(prev => {
      const updated = [...prev, {
        time: now.toLocaleTimeString(),
        responseTime: newStats.performance.avgResponseTime,
        requests: newStats.requests,
        errors: newStats.errors
      }];
      return updated.slice(-20); // Mantieni solo gli ultimi 20 punti
    });
  };

  // Controlli servizio
  const handleServiceControl = async (action: 'start' | 'stop' | 'restart') => {
    try {
      setLoading(true);
      const response = await fetch(`/api/integration/${action}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        await loadDashboardData();
        setNotifications(prev => [...prev, {
          id: Date.now(),
          type: 'success',
          message: `Servizio ${action === 'start' ? 'avviato' : action === 'stop' ? 'fermato' : 'riavviato'} con successo`
        }]);
      } else {
        throw new Error(`Errore ${action} servizio`);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : `Errore ${action} servizio`);
    } finally {
      setLoading(false);
    }
  };

  // Sincronizzazione manuale
  const handleManualSync = async (type: string) => {
    try {
      setLoading(true);
      const response = await fetch('/api/integration/sync', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ type })
      });

      if (response.ok) {
        const result = await response.json();
        setNotifications(prev => [...prev, {
          id: Date.now(),
          type: 'success',
          message: `Sincronizzazione ${type} completata: ${result.data.processed} elementi processati`
        }]);
        setSyncDialog(false);
        await loadDashboardData();
      } else {
        throw new Error('Errore sincronizzazione');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Errore sincronizzazione');
    } finally {
      setLoading(false);
    }
  };

  // Componenti di stato
  const StatusChip: React.FC<{ status: string }> = ({ status }) => {
    const getStatusProps = (status: string) => {
      switch (status) {
        case 'healthy':
          return { color: 'success' as const, icon: <CheckCircle /> };
        case 'degraded':
          return { color: 'warning' as const, icon: <Warning /> };
        case 'unhealthy':
          return { color: 'error' as const, icon: <Error /> };
        default:
          return { color: 'default' as const, icon: <Info /> };
      }
    };

    const props = getStatusProps(status);
    return <Chip {...props} label={status} size="small" />;
  };

  // Grafici
  const performanceChartData = {
    labels: performanceData.map(d => d.time),
    datasets: [
      {
        label: 'Tempo Risposta (ms)',
        data: performanceData.map(d => d.responseTime),
        borderColor: 'rgb(75, 192, 192)',
        backgroundColor: 'rgba(75, 192, 192, 0.2)',
        tension: 0.1
      }
    ]
  };

  const operationsChartData = stats ? {
    labels: ['Sync', 'Booking', 'Notifiche'],
    datasets: [{
      data: [stats.syncOperations, stats.bookingOperations, stats.notifications],
      backgroundColor: ['#FF6384', '#36A2EB', '#FFCE56']
    }]
  } : null;

  const errorRateData = {
    labels: performanceData.map(d => d.time),
    datasets: [
      {
        label: 'Richieste',
        data: performanceData.map(d => d.requests),
        backgroundColor: 'rgba(54, 162, 235, 0.5)'
      },
      {
        label: 'Errori',
        data: performanceData.map(d => d.errors),
        backgroundColor: 'rgba(255, 99, 132, 0.5)'
      }
    ]
  };

  if (loading && !serviceStatus) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ flexGrow: 1, p: 3 }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          Dashboard Integrazione
        </Typography>
        <Box>
          <Tooltip title="Aggiorna">
            <IconButton onClick={loadDashboardData} disabled={loading}>
              <Refresh />
            </IconButton>
          </Tooltip>
          <Tooltip title="Configurazione">
            <IconButton onClick={() => setConfigDialog(true)}>
              <Settings />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Notifiche */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {notifications.map(notification => (
        <Alert
          key={notification.id}
          severity={notification.type}
          sx={{ mb: 1 }}
          onClose={() => setNotifications(prev => prev.filter(n => n.id !== notification.id))}
        >
          {notification.message}
        </Alert>
      ))}

      {/* Controlli principali */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box>
              <Typography variant="h6">Stato Servizio</Typography>
              <Box display="flex" alignItems="center" gap={2} mt={1}>
                <StatusChip status={serviceStatus?.status || 'unknown'} />
                <Typography variant="body2">
                  Uptime: {serviceStatus ? Math.floor(serviceStatus.uptime / 1000 / 60) : 0} minuti
                </Typography>
              </Box>
            </Box>
            <Box display="flex" gap={1}>
              {!isRunning ? (
                <Button
                  variant="contained"
                  color="success"
                  startIcon={<PlayArrow />}
                  onClick={() => handleServiceControl('start')}
                  disabled={loading}
                >
                  Avvia
                </Button>
              ) : (
                <>
                  <Button
                    variant="contained"
                    color="error"
                    startIcon={<Stop />}
                    onClick={() => handleServiceControl('stop')}
                    disabled={loading}
                  >
                    Ferma
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<Refresh />}
                    onClick={() => handleServiceControl('restart')}
                    disabled={loading}
                  >
                    Riavvia
                  </Button>
                </>
              )}
              <Button
                variant="outlined"
                startIcon={<Sync />}
                onClick={() => setSyncDialog(true)}
                disabled={!isRunning || loading}
              >
                Sincronizza
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={activeTab} onChange={(_, newValue) => setActiveTab(newValue)}>
          <Tab label="Panoramica" icon={<Assessment />} />
          <Tab label="Performance" icon={<Speed />} />
          <Tab label="Servizi" icon={<Cloud />} />
          <Tab label="Operazioni" icon={<Timeline />} />
        </Tabs>
      </Box>

      {/* Tab Content */}
      {activeTab === 0 && (
        <Grid container spacing={3}>
          {/* Statistiche principali */}
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" gap={2}>
                  <NetworkCheck color="primary" />
                  <Box>
                    <Typography variant="h4">{stats?.requests || 0}</Typography>
                    <Typography variant="body2" color="textSecondary">
                      Richieste Totali
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" gap={2}>
                  <Sync color="success" />
                  <Box>
                    <Typography variant="h4">{stats?.syncOperations || 0}</Typography>
                    <Typography variant="body2" color="textSecondary">
                      Sincronizzazioni
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" gap={2}>
                  <Schedule color="info" />
                  <Box>
                    <Typography variant="h4">{stats?.bookingOperations || 0}</Typography>
                    <Typography variant="body2" color="textSecondary">
                      Prenotazioni
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" gap={2}>
                  <Error color="error" />
                  <Box>
                    <Typography variant="h4">{stats?.errors || 0}</Typography>
                    <Typography variant="body2" color="textSecondary">
                      Errori
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Grafico operazioni */}
          {operationsChartData && (
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Distribuzione Operazioni
                  </Typography>
                  <Box height={300}>
                    <Doughnut data={operationsChartData} options={{ maintainAspectRatio: false }} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          )}

          {/* Stato servizi */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Stato Servizi
                </Typography>
                <List>
                  {serviceStatus?.services && Object.entries(serviceStatus.services).map(([name, service]) => (
                    <ListItem key={name}>
                      <ListItemIcon>
                        <HealthAndSafety color={service.status === 'healthy' ? 'success' : 'error'} />
                      </ListItemIcon>
                      <ListItemText
                        primary={name}
                        secondary={
                          <Box display="flex" alignItems="center" gap={1}>
                            <StatusChip status={service.status} />
                            {service.responseTime && (
                              <Typography variant="caption">
                                {service.responseTime}ms
                              </Typography>
                            )}
                          </Box>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {activeTab === 1 && (
        <Grid container spacing={3}>
          {/* Grafico performance */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Performance nel Tempo
                </Typography>
                <Box height={400}>
                  <Line data={performanceChartData} options={{ maintainAspectRatio: false }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Grafico errori */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Richieste vs Errori
                </Typography>
                <Box height={300}>
                  <Bar data={errorRateData} options={{ maintainAspectRatio: false }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {activeTab === 2 && (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Dettagli Servizi
                </Typography>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Servizio</TableCell>
                        <TableCell>Stato</TableCell>
                        <TableCell>Uptime</TableCell>
                        <TableCell>Ultimo Check</TableCell>
                        <TableCell>Tempo Risposta</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {serviceStatus?.services && Object.entries(serviceStatus.services).map(([name, service]) => (
                        <TableRow key={name}>
                          <TableCell>{name}</TableCell>
                          <TableCell><StatusChip status={service.status} /></TableCell>
                          <TableCell>{Math.floor(service.uptime / 1000 / 60)} min</TableCell>
                          <TableCell>{new Date(service.lastCheck).toLocaleString()}</TableCell>
                          <TableCell>{service.responseTime || 'N/A'}ms</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {activeTab === 3 && (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Statistiche Operazioni
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <Paper sx={{ p: 2 }}>
                      <Typography variant="subtitle1">Sync Manager</Typography>
                      <Typography variant="body2">
                        Ultima sync: {stats?.services.syncManager?.lastSync || 'Mai'}
                      </Typography>
                      <Typography variant="body2">
                        Totale sincronizzati: {stats?.services.syncManager?.totalSynced || 0}
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Paper sx={{ p: 2 }}>
                      <Typography variant="subtitle1">Booking Manager</Typography>
                      <Typography variant="body2">
                        Prenotazioni in cache: {stats?.services.bookingManager?.cachedBookings || 0}
                      </Typography>
                      <Typography variant="body2">
                        Operazioni pending: {stats?.services.bookingManager?.pendingOperations || 0}
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Paper sx={{ p: 2 }}>
                      <Typography variant="subtitle1">Notification Service</Typography>
                      <Typography variant="body2">
                        Sottoscrittori: {stats?.services.notificationService?.subscribers || 0}
                      </Typography>
                      <Typography variant="body2">
                        Regole alert: {stats?.services.notificationService?.alertRules || 0}
                      </Typography>
                    </Paper>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Dialog Configurazione */}
      <Dialog open={configDialog} onClose={() => setConfigDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Configurazione Integrazione</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={config.autoSync}
                    onChange={(e) => setConfig(prev => ({ ...prev, autoSync: e.target.checked }))}
                  />
                }
                label="Sincronizzazione Automatica"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Intervallo Sync (ms)"
                type="number"
                value={config.syncInterval}
                onChange={(e) => setConfig(prev => ({ ...prev, syncInterval: parseInt(e.target.value) }))}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Risoluzione Conflitti</InputLabel>
                <Select
                  value={config.conflictResolution}
                  onChange={(e) => setConfig(prev => ({ ...prev, conflictResolution: e.target.value }))}
                >
                  <MenuItem value="healthnet_priority">Priorità HealthNET</MenuItem>
                  <MenuItem value="recup_priority">Priorità RECUP</MenuItem>
                  <MenuItem value="timestamp">Timestamp</MenuItem>
                  <MenuItem value="manual">Revisione Manuale</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={config.notificationsEnabled}
                    onChange={(e) => setConfig(prev => ({ ...prev, notificationsEnabled: e.target.checked }))}
                  />
                }
                label="Notifiche Abilitate"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfigDialog(false)}>Annulla</Button>
          <Button variant="contained" onClick={() => setConfigDialog(false)}>
            Salva
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog Sincronizzazione */}
      <Dialog open={syncDialog} onClose={() => setSyncDialog(false)}>
        <DialogTitle>Sincronizzazione Manuale</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Seleziona il tipo di sincronizzazione da eseguire:
          </Typography>
          <Box display="flex" flexDirection="column" gap={1}>
            <Button onClick={() => handleManualSync('full')} disabled={loading}>
              Sincronizzazione Completa
            </Button>
            <Button onClick={() => handleManualSync('execution_units')} disabled={loading}>
              Solo Unità Eroganti
            </Button>
            <Button onClick={() => handleManualSync('diaries')} disabled={loading}>
              Solo Agende
            </Button>
            <Button onClick={() => handleManualSync('bookings')} disabled={loading}>
              Solo Prenotazioni
            </Button>
            <Button onClick={() => handleManualSync('patients')} disabled={loading}>
              Solo Pazienti
            </Button>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSyncDialog(false)}>Annulla</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default IntegrationDashboard;